/**
 * Centralized error handling types and utilities
 *
 * Provides consistent error types, classification, and handling across the application.
 * Improves debugging and user experience by standardizing error responses.
 */

/**
 * Standard API error response format
 */
export interface ApiErrorResponse {
    error: {
        code: string;
        message: string;
        details?: unknown;
    };
}

/**
 * Error categories for better handling and user messaging
 */
export const ErrorCategory = {
    NETWORK: "network",
    AUTHENTICATION: "authentication",
    AUTHORIZATION: "authorization",
    VALIDATION: "validation",
    RATE_LIMIT: "rate_limit",
    QUOTA_EXCEEDED: "quota_exceeded",
    AI_SERVICE: "ai_service",
    SERVER_ERROR: "server_error",
    UNKNOWN: "unknown",
} as const;

export type ErrorCategory = (typeof ErrorCategory)[keyof typeof ErrorCategory];

/**
 * Enhanced API error with additional context and categorization
 */
export class ApiError extends Error {
    public readonly category: ErrorCategory;
    public readonly code: string;
    public readonly statusCode: number;
    public readonly details?: unknown;
    public readonly timestamp: Date;

    constructor(
        message: string,
        code: string,
        statusCode: number,
        category: ErrorCategory = ErrorCategory.UNKNOWN,
        details?: unknown
    ) {
        super(message);
        this.name = "ApiError";
        this.category = category;
        this.code = code;
        this.statusCode = statusCode;
        this.details = details;
        this.timestamp = new Date();

        // Maintain proper stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }
    }

    /**
     * Checks if this error should trigger a retry
     */
    get isRetryable(): boolean {
        return this.category === ErrorCategory.NETWORK || (this.statusCode >= 500 && this.statusCode < 600);
    }

    /**
     * Checks if this error should show a user-friendly message
     */
    get isUserFacing(): boolean {
        return this.category !== ErrorCategory.UNKNOWN && this.category !== ErrorCategory.SERVER_ERROR;
    }

    /**
     * Gets a user-friendly error message
     */
    get userMessage(): string {
        switch (this.category) {
            case ErrorCategory.NETWORK:
                return "Connection problem. Please check your internet and try again.";
            case ErrorCategory.AUTHENTICATION:
                return "Please sign in to continue.";
            case ErrorCategory.AUTHORIZATION:
                return "You don't have permission to perform this action.";
            case ErrorCategory.VALIDATION:
                return this.message; // Use specific validation message
            case ErrorCategory.RATE_LIMIT:
                return "Too many requests. Please wait a moment and try again.";
            case ErrorCategory.QUOTA_EXCEEDED:
                return "You've reached your usage limit. Please try again later.";
            case ErrorCategory.AI_SERVICE:
                return "AI service is temporarily unavailable. Please try again.";
            default:
                return "Something went wrong. Please try again.";
        }
    }
}

/**
 * Classifies an API error response into appropriate category
 */
export function classifyError(statusCode: number, errorCode: string): ErrorCategory {
    // Network errors (no response)
    if (statusCode === 0) {
        return ErrorCategory.NETWORK;
    }

    // Authentication errors
    if (statusCode === 401) {
        return ErrorCategory.AUTHENTICATION;
    }

    // Authorization errors
    if (statusCode === 403) {
        return ErrorCategory.AUTHORIZATION;
    }

    // Validation errors
    if (statusCode === 400) {
        if (errorCode.includes("VALIDATION") || errorCode.includes("INVALID")) {
            return ErrorCategory.VALIDATION;
        }
    }

    // Rate limiting
    if (statusCode === 429) {
        return ErrorCategory.RATE_LIMIT;
    }

    // Quota exceeded
    if (errorCode.includes("QUOTA") || errorCode.includes("LIMIT")) {
        return ErrorCategory.QUOTA_EXCEEDED;
    }

    // AI service errors
    if (errorCode.includes("AI") || errorCode.includes("GENERATION") || errorCode.includes("OPENAI")) {
        return ErrorCategory.AI_SERVICE;
    }

    // Server errors
    if (statusCode >= 500) {
        return ErrorCategory.SERVER_ERROR;
    }

    return ErrorCategory.UNKNOWN;
}

/**
 * Creates an ApiError from a Response and error data
 */
export function createApiError(response: Response, errorData: ApiErrorResponse, operation: string): ApiError {
    const category = classifyError(response.status, errorData.error.code);

    return new ApiError(
        `${operation} failed: ${errorData.error.message}`,
        errorData.error.code,
        response.status,
        category,
        errorData.error.details
    );
}

/**
 * Creates a network error for failed fetch requests
 */
export function createNetworkError(operation: string, originalError?: Error): ApiError {
    return new ApiError(
        `${operation} failed: Network error`,
        "NETWORK_ERROR",
        0,
        ErrorCategory.NETWORK,
        originalError?.message
    );
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

/**
 * Helper to get user-friendly message from any error
 */
export function getUserErrorMessage(error: unknown): string {
    if (isApiError(error)) {
        return error.userMessage;
    }

    if (error instanceof Error) {
        return error.message;
    }

    return "An unexpected error occurred.";
}
